// $revealed(value): what every x-trap reads, e.g. x-trap.noscroll.inert="$revealed(open)".
//
// x-show reveals an element on the next animation frame (the first frame of its enter
// transition when it has one), but x-trap starts focus-trap 15ms after its expression turns
// true. When that frame lands later, the dialog is still display: none, focus-trap finds
// nothing it can focus, and it never tries again: focus stays behind the dialog and a screen
// reader never hears it open.
//
// This is false until `value` has been true for two animation frames, so the trap starts once
// the dialog is on screen. It follows the value however it changes (an event, a click,
// @entangle), and turns false as soon as the value does, with one exception below.
const gates = new WeakMap();

// x-trap's own delay: setTimeout(() => trap.activate(), 15) in livewire.js's "trap" directive.
// FocusTrapWaitsForRevealTest reads the bundle and fails if it changes.
export const X_TRAP_ACTIVATION_DELAY_MS = 15;

export default function revealed(el, { Alpine }) {
    return (value) => {
        const open = Boolean(value);

        // Alpine's morph evaluates directives on a detached copy of each element it patches.
        // The copy has no wait of its own, so it gets the plain value, as before this helper.
        if (!el.isConnected) {
            return open;
        }

        let gate = gates.get(el);
        if (!gate) {
            gate = { open: false, generation: 0, cycle: 0, watching: false, settled: false, closeHeld: false, state: Alpine.reactive({ ready: false }) };
            gates.set(el, gate);
        }

        const release = () => {
            gate.closeHeld = false;
            gate.state.ready = false;
        };

        if (open !== gate.open) {
            gate.open = open;
            // Frames still pending from an earlier open must not count toward this one.
            const generation = ++gate.generation;

            if (open) {
                // A close still held below never reached x-trap, so the trap stays as it is.
                gate.closeHeld = false;
                if (!gate.state.ready) {
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                        if (gate.generation === generation) {
                            gate.cycle++;
                            gate.watching = false;
                            gate.settled = false;
                            gate.state.ready = true;
                        }
                    }));
                }
            } else if (gate.state.ready) {
                // The exception. x-trap never re-checks its pending activate(), and focus-trap
                // applies .inert later still. A close that reaches x-trap before both have run
                // releases a trap that is not fully on, and what runs next traps the hidden
                // dialog or leaves the page aria-hidden. So until then the close is held.
                if (gate.settled) {
                    release();
                } else {
                    gate.closeHeld = true;
                }
            }
        }

        const ready = gate.state.ready;

        if (ready && !gate.watching) {
            gate.watching = true;
            const cycle = gate.cycle;
            // x-trap arms its timer after this evaluation returns, so the same delay armed
            // from a microtask fires right after its activate(). A close held until then is let
            // go before focus-trap moves focus, which cancels the rest. Otherwise focus-trap
            // moves focus on a setTimeout(0) and queues .inert on an Alpine.nextTick; a
            // setTimeout(0) and a nextTick of our own run after both.
            queueMicrotask(() => setTimeout(() => {
                if (gate.cycle !== cycle) {
                    return;
                }
                if (gate.closeHeld) {
                    release();
                    return;
                }
                setTimeout(() => Alpine.nextTick(() => {
                    if (gate.cycle !== cycle) {
                        return;
                    }
                    gate.settled = true;
                    if (gate.closeHeld) {
                        release();
                    }
                }), 0);
            }, X_TRAP_ACTIVATION_DELAY_MS));
        }

        return ready;
    };
}
